// The small HTTP surface: create a room, look one up, list the characters.
import type { RoleCard, RoleType } from "../protocol";

export interface RoomInfo {
  code: string;
  phase: string;
  player_count: number;
  min_players: number;
  max_players: number;
  can_join: boolean;
}

/** How many of each kind a game of this size starts with, before any character changes it. */
export type SetupCount = { players: number } & Record<RoleType, number>;

export interface ScriptInfo {
  id: string;
  name: string;
  description: string;
  roles: RoleCard[];
  counts: SetupCount[];
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function detail(resp: Response): Promise<string> {
  try {
    const body = (await resp.json()) as { detail?: string };
    return body.detail ?? resp.statusText;
  } catch {
    return resp.statusText;
  }
}

export async function createRoom(): Promise<string> {
  // no request body: the server does not read one
  const resp = await fetch("/api/rooms", { method: "POST" });
  if (!resp.ok) throw new ApiError(resp.status, await detail(resp));
  return ((await resp.json()) as { code: string }).code;
}

export async function getRoom(code: string): Promise<RoomInfo | null> {
  const resp = await fetch(`/api/rooms/${encodeURIComponent(code)}`);
  if (resp.status === 404) return null;
  if (!resp.ok) throw new ApiError(resp.status, await detail(resp));
  return (await resp.json()) as RoomInfo;
}

export async function getScripts(): Promise<ScriptInfo[]> {
  const resp = await fetch("/api/scripts");
  if (!resp.ok) throw new ApiError(resp.status, await detail(resp));
  return (await resp.json()) as ScriptInfo[];
}

const CODE_ALPHABET = /^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{5}$/;

export function normalizeCode(raw: string): string {
  return raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
}

export function isValidCode(code: string): boolean {
  return CODE_ALPHABET.test(code);
}
